import { useReduxState } from 'hooks';
import { FlexContainer, Search, SortBy } from 'components/common';
import { DropdownSelectable } from 'components/common/dropdown';
import { Filters, FiltersActive } from 'components/common/filter';

import type { SortIndividualAchievements } from 'shared-types';

export type IndividualAchievementFieldsView = 'all fields' | 'default';

interface Props {
  setSortBy: (sortBy: SortIndividualAchievements['sortBy']) => void;
  setView: (view: IndividualAchievementFieldsView) => void;
  sortBy: SortIndividualAchievements['sortBy'];
  view: IndividualAchievementFieldsView;
}

const AchievementsIndividualSubHeader = ({
  setSortBy,
  setView,
  sortBy,
  view
}: Props) => {
  const {
    language,
    messages,
    serverConfig: {
      individualAchievementCustomAttributes,
      individualAchievementsDisplayForAdmins,
      userUseName
    },
    user
  } = useReduxState();

  const handleSortChange = (sortValue) => {
    setSortBy(sortValue.sortBy);
  };

  const sortOptions = [
    userUseName
      ? { text: messages.commonUser, value: 'name' }
      : { text: messages.commonUsername, value: 'username' },
    { text: messages.commonTitle, value: 'title' },
    { text: messages.completionsCompletionDate, value: 'completionDate' },
    { text: messages.commonExpirationDate, value: 'expirationDate' }
  ];

  const isAllowedToSeeOthersAchievements =
    (individualAchievementsDisplayForAdmins && user?.roles.includes('admin')) ||
    user?.roles.includes('superadmin');

  const productAttributeFilters = individualAchievementCustomAttributes?.map(
    (attribute) => {
      const { max, min, type } = attribute;

      const filterData: React.ComponentProps<
        typeof Filters
      >['customFilters'][0] = {
        allowMultiple: true,
        field: `customAttribute.${attribute.key}`,
        icon: 'tag',
        position: 4 + attribute.number,
        title: attribute.titleData?.[language] || attribute.title
      };

      if (type === 'Boolean') {
        filterData.items = [
          { label: messages.commonYes, value: 'true' },
          { label: messages.commonNo, value: 'false' },
          { label: messages.commonNotSet, value: 'not set' },
          {
            label: messages.userAttributesNoIncludingNotSet,
            value: 'no including not set'
          }
        ];
      } else if (type === 'Number') {
        filterData.itemsAll = {
          label: messages.groupsUserAttributesAny,
          value: 'anyValueAllowed'
        };
        filterData.items = Array.from(
          Array(Math.max(max - min + 1, 1)),
          (n, i) => ({
            label: `${i + min}`,
            value: `${i + min}`
          })
        );
        filterData.selectAllOnClick = true;
      } else if (type === 'ArrayWithOptions' || type === 'StringWithOptions') {
        const optionsSet = new Set<string>();
        attribute.options.forEach((option) =>
          option.options.forEach((value) => optionsSet.add(value))
        );
        const mergedOptions = Array.from(optionsSet);

        filterData.itemsAll = {
          label: messages.groupsUserAttributesAny,
          value: 'anyValueAllowed'
        };
        filterData.items = mergedOptions.map((option) => ({
          label: option,
          value: option
        }));
        filterData.selectAllOnClick = true;
      } else {
        // type is String, Array or Text
        filterData.input = true;
      }

      return filterData;
    }
  );

  return (
    <>
      <FlexContainer
        style={{
          flexWrap: 'wrap',
          gap: 10,
          justifyContent: 'space-between',
          width: '100%'
        }}
      >
        <FlexContainer style={{ flexWrap: 'wrap', gap: 10 }}>
          <div style={{ minWidth: 240 }}>
            <Search
              placeholder={messages.achievementSearch}
              size="header"
              searchKey="titleSearch"
              withUrl={true}
            />
          </div>

          {isAllowedToSeeOthersAchievements ? (
            <Filters
              allowMultiple={{
                groups: true,
                supervisors: true,
                userAttributes: true
              }}
              customFilters={[
                {
                  icon: 'star',
                  field: 'excludeOtherUsers',
                  items: [
                    {
                      label: messages.achievementsIndividualShowOnlyMy,
                      value: 'yes'
                    }
                  ],
                  position: 0,
                  title: messages.achievementsMyAchievements
                },
                {
                  allowMultiple: true,
                  field: 'completionStatus',
                  icon: ['far', 'clock'],
                  items: [
                    { label: messages.commonValid, value: 'valid' },
                    { label: messages.commonExpired, value: 'expired' },
                    {
                      label: messages.profileTimelineUpcoming,
                      value: 'upcoming'
                    }
                  ],
                  position: 1,
                  title: messages.commonStatus
                },
                ...productAttributeFilters
              ]}
              filterId="individual-achievement-users"
              filters={{
                accountAccess: true,
                accountIdentifier: true,
                accountLogin: true,
                accountType: {
                  items: [
                    { label: messages.commonUser, value: 'user' },
                    { label: messages.usersAdmin, value: 'admin' },
                    { label: messages.usersSuperadmin, value: 'superadmin' },
                    {
                      label: messages.commonUserExclude,
                      value: 'excludeUser'
                    },
                    {
                      label: messages.usersAdminExclude,
                      value: 'excludeAdmin'
                    },
                    {
                      label: messages.usersSuperadminExclude,
                      value: 'excludeSuperadmin'
                    }
                  ]
                },
                groups: true,
                supervisors: true,
                userAttributes: true
              }}
            />
          ) : null}
        </FlexContainer>

        <FlexContainer style={{ gap: 10 }}>
          <DropdownSelectable
            category="lighter"
            defaultText={`${messages.commonSelect}...`}
            defaultValue={view}
            dropdownTextMinWidth="120px"
            onChange={setView}
            options={[
              { text: messages.commonDefault, value: 'default' },
              { text: messages.commonAllFields, value: 'all fields' }
            ]}
            selectedValueBefore={`${messages.commonView}:`}
            showSelectedText={true}
            size="header"
            titleMaxWidth={200}
            value={view}
          />
          <SortBy
            onChange={handleSortChange}
            options={sortOptions}
            value={sortBy}
          />
        </FlexContainer>
      </FlexContainer>

      {user?.roles.includes('admin') && (
        <div style={{ display: 'flex', gap: 10 }}>
          <FiltersActive filterId="individual-achievement-users" />
        </div>
      )}
    </>
  );
};

export default AchievementsIndividualSubHeader;
